import React, { useCallback, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import styled from 'styled-components';
import {
  FeedbackCommentBodyWithIndex,
  INDICATOR_SUMMARY_QUESTION_ID,
  SelectedFeedback,
} from '../../NPOFeedback/utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { GuidanceTextResponse } from '../../ReportBuilder/Components/utils';
import { QuestionTextFromAnswers } from '../../ReportBuilder/Components/QuestionText';
import {
  FeedbackCommentProps,
  FeedbackComments as FeedbackCommentsType,
  FeedbackSectionId,
  toFeedbackCommentBody,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  Answer,
  deleteQuestionLevelComment,
  toDatabaseUserType,
  updateQuestionLevelComment,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import {
  HandleUpdateAnswer,
  ResolvedFeedback,
  useFeedback,
  VerifyFeedback,
} from '../FeedbackProvider';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import MakeEditsModal from '../../NPOFeedback/MakeEdits/MakeEditsModal';
import {
  FeedbackQuestion as FeedbackQuestionApi,
  FeedbackQuestionsById,
} from '../../NPOFeedback/MakeEdits/api';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import { Report } from 'shared/components/WelcomeBack/utils';
import ImpactDetailComments from './ImpactDetailComments';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import FeedbackAnswer from './FeedbackAnswer';
import DonorNameWarning from '../../ReportsBeta/LevelOneFeedback/DonorNameWarning';
import { hasDonorName } from 'shared/utils/donorNameMatch';
import FeedbackComments from './FeedbackComments';
import FeedbackBottomBar from './FeedbackBottomBar';
import FeedbackSupport from './FeedbackSupport';
import FeedbackSectionButtons from './FeedbackSectionButtons';

const getFeedbackByQuestionIds = (
  questionId: number,
  reportId: number,
  sectionId: FeedbackSectionId,
  allComments: FeedbackCommentsType,
): { [questionId: number]: FeedbackCommentBodyWithIndex[] } => {
  return {
    80003: toFeedbackCommentBody(
      questionId,
      reportId,
      sectionId,
      allComments[80003] || [],
    ),
    80005: toFeedbackCommentBody(
      questionId,
      reportId,
      sectionId,
      allComments[80005] || [],
    ),
    80007: toFeedbackCommentBody(
      questionId,
      reportId,
      sectionId,
      allComments[80007] || [],
    ),
  };
};

const hideQuestionText = [50001, 50002, 60001];

const getFeedbackStatusText = (
  questionId: number,
  resolvedFeedback: ResolvedFeedback,
  verifyFeedback: VerifyFeedback,
) => {
  const resolve = resolvedFeedback.questionsById[questionId];
  const verify = verifyFeedback.questionsById[questionId];
  if (resolve) {
    return (
      <TrueImpactText className="feedback-show-status">
        Feedback status:
        <FontAwesomeIcon color={COLORS.success} icon={faCheckCircle} />{' '}
        <span>Resolved</span>
      </TrueImpactText>
    );
  }
  if (verify === 'verified') {
    return (
      <TrueImpactText className="feedback-show-status">
        Feedback status:
        <FontAwesomeIcon color={COLORS.success} icon={faCheckCircle} />{' '}
        <span>Changes accepted</span>
      </TrueImpactText>
    );
  }
  if (verify === 'rejected') {
    return (
      <TrueImpactText className="feedback-show-status">
        Feedback status:
        <FontAwesomeIcon color={COLORS.critical} icon={faCheckCircle} />{' '}
        <span>Requested additional changes</span>
      </TrueImpactText>
    );
  }
  return null;
};

const getReportObject = (
  programName: string,
  reportId: number,
  reportStartDate: string,
  reportEndDate: string,
  feedback: FeedbackDashboardCardProps,
): Report => ({
  programName,
  dbId: reportId,
  stage: feedback.stage,
  endDate: reportEndDate,
  startDate: reportStartDate,
  fiscalYearStart: reportStartDate,
  fiscalYearEnd: reportEndDate,
  errors: feedback.errors,
  name: feedback.name,
  programId: feedback.programId,
  feedbackType: feedback.feedbackType,
  portfolioName: feedback.portfolioName || '',
  sponsorName: null,
  tasks: '',
  totalDonations: feedback.totalDonations,
  year: feedback.year || '',
  status: feedback.status,
  version: feedback.version,
  programManager: undefined,
  warnings: feedback.warnings,
});

const mapFeedbackComments = (
  reportId: number,
  sectionId: FeedbackSectionId,
  comments: FeedbackCommentProps[],
): FeedbackCommentBodyWithIndex[] => {
  return comments.map((comment, index) => {
    const f: FeedbackCommentBodyWithIndex = {
      details: comment.details,
      commentId: comment.commentId,
      createdAt: comment.createdAt,
      comment: comment.text,
      index,
      userName: comment.name,
      userEmail: comment.email || '',
      reportId: reportId,
      sectionId: sectionId,
      userId: comment.userId,
      userType: toDatabaseUserType(comment.accountType || 'ti staff'),
    };
    return f;
  });
};

interface FeedbackQuestionProps {
  reportId: number;
  questionId: number;
  answer: Answer;
  isDone: boolean;
  isLastIndex: boolean;
  reportStartDate: string;
  reportEndDate: string;
  programName: string;
  donorName: string;
  selectedIndex: number;
  reportYear: string | undefined;
  comments: FeedbackCommentProps[];
  guidanceText: GuidanceTextResponse;
  feedback: FeedbackDashboardCardProps;
  feedbackByQuestions: SelectedFeedback[];
  feedbackQuestionsById: FeedbackQuestionsById;
  answersById: {
    [id: number]: Answer;
  };
  handleNext: () => unknown;
  handlePrevious: () => unknown;
  reloadAnswers: () => unknown;
  updateAnswer: (a: HandleUpdateAnswer, s: FeedbackSectionId) => unknown;
  markAsDone: () => unknown;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const StyledDiv = styled.div`
  padding-bottom: 80px;
  margin-top: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  .feedback-section-header {
    padding-left: ${SPACERS.large};
    margin-bottom: ${SPACERS.medium};
  }
  .feedback-show-question {
    cursor: pointer;
    text-align: center;
    color: ${COLORS.ctaPrimary};
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.extraLarge};
  }
  .feedback-show-status {
    svg {
      margin-left: ${SPACERS.tiny};
    }
    span {
      font-weight: bold;
    }
  }
  .mb-3 {
    margin-bottom: ${SPACERS.medium};
  }
`;

const FeedbackQuestion: React.FC<FeedbackQuestionProps> = ({
  reportId,
  answer,
  questionId,
  answersById,
  selectedIndex,
  guidanceText,
  reportYear,
  reportStartDate,
  reportEndDate,
  programName,
  donorName,
  comments,
  isDone,
  isLastIndex,
  feedback,
  feedbackQuestionsById,
  feedbackByQuestions,
  handleNext,
  updateAnswer,
  markAsDone,
  handlePrevious,
  setSelectedIndex,
}) => {
  const {
    comments: allComments,
    verifyFeedback,
    resolvedFeedback,
    isVerificationStatus,
    addOrUpdateCommentState,
    deleteCommentState,
  } = useFeedback();
  const showAnswers =
    feedback.status === 'Awaiting TI Verification' ||
    feedback.status === 'Awaiting Funder Verification';
  const { userId, userType, email, name } = useUserActions();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  const question = feedbackByQuestions[selectedIndex];
  const sectionId = question.section.sectionId;

  const openEditModal = useCallback(() => setEditModalOpen(true), [
    setEditModalOpen,
  ]);

  const closeEditModal = useCallback(() => setEditModalOpen(false), [
    setEditModalOpen,
  ]);

  const onSuccessSave = (c: FeedbackCommentProps, i: number): void => {
    addOrUpdateCommentState(questionId, c, i, false);
  };
  const onSuccessDelete = (i: number): void => {
    deleteCommentState(questionId, i);
  };
  const currentUserId = userId ? userId.toString() : undefined;

  return (
    <>
      <StyledDiv>
        <div className="feedback-section-header">
          <TrueImpactText variant="heading">
            Section:{' '}
            <span className="normal-font">{question.section.label}</span>
          </TrueImpactText>
          {hideQuestionText.includes(questionId) ? null : (
            <TrueImpactText variant="heading-subheading">
              <QuestionTextFromAnswers
                answer={answer}
                answersById={answersById}
                questionId={questionId}
                guidanceText={guidanceText}
                reportYear={reportYear}
                reportStartDate={reportStartDate}
                reportEndDate={reportEndDate}
                programName={programName}
                donorName={donorName}
              />
            </TrueImpactText>
          )}
          {questionId === INDICATOR_SUMMARY_QUESTION_ID && isVerificationStatus
            ? null
            : showAnswers && (
                <FeedbackAnswer answer={answer} questionId={questionId} />
              )}
          {hasDonorName(answer.text, donorName) && (
            <DonorNameWarning variant="short" />
          )}
        </div>
        {questionId === INDICATOR_SUMMARY_QUESTION_ID ? (
          <ImpactDetailComments
            answer={answer}
            reportId={reportId}
            comments={comments}
            answersById={answersById}
            currentUserId={currentUserId}
            currentUserName={name}
            currentUserEmail={email}
            currentUserType={userType}
            reportStartDate={reportStartDate}
            reportEndDate={reportEndDate}
            isFeedbackVerification={isVerificationStatus}
            openEditModal={openEditModal}
            onSuccessSave={onSuccessSave}
            onSuccessDelete={onSuccessDelete}
            handleDelete={deleteQuestionLevelComment}
            handleSave={updateQuestionLevelComment}
          />
        ) : (
          <FeedbackComments
            reportId={reportId}
            questionId={questionId}
            sectionId={sectionId}
            comments={comments}
            isCustomQuestion={false}
          />
        )}
        {getFeedbackStatusText(questionId, resolvedFeedback, verifyFeedback)}
        <FeedbackSectionButtons
          question={question}
          isCustomQuestion={false}
          questionId={questionId}
          selectedIndex={selectedIndex}
          isVerificationStatus={isVerificationStatus}
          handleNext={handleNext}
          openEditModal={openEditModal}
        />
        {!isVerificationStatus && (
          <FeedbackSupport
            reportId={reportId}
            questionId={questionId}
            className="feedback-show-question"
            label="I have a question"
          />
        )}
        <FeedbackBottomBar
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          selectedIndex={selectedIndex}
          isDone={isDone}
          isLastIndex={isLastIndex}
        />
      </StyledDiv>
      <MakeEditsModal
        open={editModalOpen}
        isFinalizedUI={false}
        report={getReportObject(
          programName,
          reportId,
          reportStartDate,
          reportEndDate,
          feedback,
        )}
        feedbackByQuestionId={getFeedbackByQuestionIds(
          questionId,
          reportId,
          sectionId,
          allComments,
        )}
        reportId={reportId}
        isError={false}
        isLoading={false}
        programName={programName}
        isAnswersLoading={false}
        questions={feedbackQuestionsById}
        feedback={mapFeedbackComments(reportId, sectionId, comments)}
        selectedSection={{
          question: {
            questionId,
            questionText: question.question?.questionText || '',
          },
          section: question.section,
        }}
        handleAnswerUpdate={(
          questionId: number,
          answer: string,
          questionText: string,
          questionType: FeedbackQuestionApi['questionType'],
        ) => {
          updateAnswer(
            {
              questionId,
              answer,
              answerId: undefined,
              ignoreClearData: false,
              id: undefined,
              startDate: undefined,
              endDate: undefined,
            },
            sectionId,
          );
        }}
        onClose={closeEditModal}
        reloadAnswers={() => null}
        markAsCompleted={markAsDone}
      />
    </>
  );
};

export default FeedbackQuestion;
